import React from "react";
import {IonContent} from "@ionic/react";
import DesktopContainer from "./DesktopContainer";
import MobileContainer from "./MobileContainer";
import './MainMapContainer.css';
import {EOperationType} from "../../../models/utils/EOperationType";
import {IDisplayedOnMap} from "../utils/IDisplayedOnMap";
import {IMapContainerProps} from "../utils/IMapContainerProps";
import GpsCoordinatesService from "../../../services/map/GpsCoordinatesService";
import MarkerService from "../../../services/map/MarkerService";
import OperationLocalService from "../../../services/storage/localStorage/OperationLocalService";

/**
 * Component managing the data to be displayed on the map.
 * Generates the markers and adapts their display according to the filters chosen by the user.
 * @author Alizée Grand
 * @version 1.0
 * @creation 05/01/2022
 * @last_update 05/01/2022
 **/
class MainMapContainer extends React.Component<{}> {

    state = {
        operations: [],
        filter: undefined,
    }

    /**
     * Is executed after the component output has been injected into the DOM.
     * Displays all data when the page is launched.
     */
    componentDidMount() {
        this.setState({operations: OperationLocalService.getOperations()});
        this.generateMarkersOnRightCoordinates();
    }

    /**
     * Method called immediately at each update of the component. It allows generating new markers on the map if the user has used a filter.
     * @param {Readonly<{}>} prevProps Props present in the state of the previous component.
     * @param {Readonly<{filter: EOperationType | undefined}>} prevState State present in the state of the previous component.
     * @param snapshot Copy of the component made before its refreshment.
     */
    componentDidUpdate(prevProps: Readonly<{}>, prevState: Readonly<{filter: undefined | EOperationType}>, snapshot?: any) {
        if(this.state.filter !== prevState.filter) {
            this.setState(
                {operations: this.state.filter !== undefined ? OperationLocalService.getOperationsByType(this.state.filter) : OperationLocalService.getOperations()}
            );
        }
        this.generateMarkersOnRightCoordinates();
    }

    /**
     * Uses different services to generate the information that will be displayed on the map.
     * @return {IDisplayedOnMap}
     */
    generateMarkersOnRightCoordinates(): IDisplayedOnMap {
        return {
            markers: MarkerService.generateMarkersFromOperations(this.state.operations, this.state.filter),
            gpsCoordinates: GpsCoordinatesService.generateGpsCoordinatesFromOperations(this.state.operations, this.state.filter)
        };
    }

    render() {

        /**
         * Changes the component's state by indicating which operations should be displayed.
         * @param {EOperationType} filter If filter is undefined, display all operations.
         * @private
         */
        const changeDisplayedData = (filter?: EOperationType) => {
            this.setState({filter: filter});
        }

        /**
         * Generates the props that will be passed to the map.
         * @type {IMapContainerProps}
         */
        const props: IMapContainerProps = {
            displayedOnMap: this.generateMarkersOnRightCoordinates(),
            displayCorrectFilter: changeDisplayedData
        }

        return (

            <IonContent>
                {/* Appears for devices larger than 1023px */}
                <IonContent id="mapDesktopMainContainer">
                    <DesktopContainer mapContainerProps={props}/>
                </IonContent>
                { /* Appears for devices less than 1024px */}
                <IonContent id="mapMobileMainContainer">
                    <MobileContainer mapContainerProps={props}/>
                </IonContent>
            </IonContent>

        );

    };

}

export default MainMapContainer;
